import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../hooks/use-is-mobile';

interface ProductNameAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  isLoading?: boolean;
  warning?: boolean;
}

export function ProductNameAutocomplete({
  value,
  onChange,
  options,
  isLoading = false,
  warning = false,
}: ProductNameAutocompleteProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <Autocomplete
      freeSolo
      fullWidth
      options={options}
      loading={isLoading}
      value={value}
      onChange={(_, newValue) => onChange(newValue ?? '')}
      inputValue={value}
      onInputChange={(_, newInput) => onChange(newInput)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('goodsReceiving.productName')}
          placeholder={t('goodsReceiving.productNamePlaceholder')}
          required
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps?.input,
              sx: {
                fontSize: isMobile ? '16px' : undefined,
                ...(warning && {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'warning.main',
                    borderWidth: 1.5,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'warning.dark',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'warning.main',
                    borderWidth: 2,
                  },
                }),
              },
            },
            inputLabel: warning
              ? {
                  ...params.slotProps?.inputLabel,
                  sx: {
                    color: 'warning.main',
                    '&.Mui-focused': { color: 'warning.main' },
                  },
                }
              : params.slotProps?.inputLabel,
          }}
        />
      )}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props}>
          <Typography variant="body1">{option}</Typography>
        </Box>
      )}
      noOptionsText={
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            p: 2,
            textAlign: 'center',
          }}
        >
          {t('goodsReceiving.noProductFound')}
        </Typography>
      }
    />
  );
}
